import React, { FC, useMemo } from 'react';
import { Heading, Icon, Image, Link, Text, Types } from '@marriott/mi-ui-library';

import { WalletAwardTile } from '../../../../molecules';
import { getTrackingWithCustomClickValue } from '../../../../modules';
import { getNuaTotalCount } from '../../hooks/useWalletAwards';
import { AEMAwardsCommon, WalletNightlyUpgradeAwardsProps } from '../../types';
import { buildSectionQuantityLabel } from '../../utils/buildSectionQuantityLabel';
import { buildWalletAwardExpirationText } from '../../utils/buildWalletAwardExpirationText';
import { isWalletAwardExpiringSoon } from '../../utils/isWalletAwardExpiringSoon';
import {
  resolveWalletEmbeddedTracking,
  resolveWalletEmptyHowToEarnTracking,
} from '../../utils/resolveWalletEmbeddedTracking';
import { StyledWalletNightlyUpgradeAwardsSection } from './WalletNightlyUpgradeAwardsSection.styles';

import { WalletNightlyUpgradeAwardsSectionStatus } from '../../types';

type WalletNuaCardViewModel = {
  id: string;
  eyebrow?: string;
  title: string;
  expirationText?: string;
  expirationUrgent?: boolean;
  statusLabel?: string;
  statusVariant: 'success' | 'neutral' | 'info';
  statusKind: WalletNightlyUpgradeAwardsSectionStatus;
  mediaImageSrc?: string;
  mediaImageAltText?: string;
};

const getStatusLabel = (status: WalletNightlyUpgradeAwardsSectionStatus, common?: AEMAwardsCommon) => {
  if (!common) return undefined;
  switch (status) {
    case WalletNightlyUpgradeAwardsSectionStatus.Available:
      return common?.availableLabel;
    case WalletNightlyUpgradeAwardsSectionStatus.Approved:
      return common?.approvedLabel;
    case WalletNightlyUpgradeAwardsSectionStatus.RequestPending:
      return common?.requestPendingLabel;
    default:
      return undefined;
  }
};

const getStatusVariant = (status: WalletNightlyUpgradeAwardsSectionStatus): 'success' | 'neutral' | 'info' => {
  switch (status) {
    case WalletNightlyUpgradeAwardsSectionStatus.Available:
      return 'success';
    case WalletNightlyUpgradeAwardsSectionStatus.RequestPending:
      return 'info';
    default:
      return 'neutral';
  }
};

const getStatusIconClass = (
  status: WalletNightlyUpgradeAwardsSectionStatus,
  common?: AEMAwardsCommon
): string | undefined => {
  if (!common) return undefined;
  switch (status) {
    case WalletNightlyUpgradeAwardsSectionStatus.Approved:
      return common?.approvedStatusIcon;
    case WalletNightlyUpgradeAwardsSectionStatus.RequestPending:
      return common?.requestPendingStatusIcon;
    default:
  }
  return undefined;
};

/**
 * Nightly Upgrade Awards section (nightlyUpgrade slice). Presentational only;
 * receives the already-fetched NUA items, the shared `common` slice, and the
 * per-type empty state content from the WalletAwardsSection orchestrator.
 */
export const WalletNightlyUpgradeAwardsSection: FC<WalletNightlyUpgradeAwardsProps> = ({
  common,
  nightlyUpgrade,
  emptyState,
  items,
  memberIconColor,
  currentLocale,
  onHowToEarnClick,
  onAwardTileClick,
}) => {
  const emptyCtaLabel = emptyState?.nightlyUpgradeEmptyLinkLabel || nightlyUpgrade?.descriptionLinkLabel;
  const emptyCtaLink = emptyState?.nightlyUpgradeEmptyLink || nightlyUpgrade?.descriptionLink;
  const emptyCtaOpenInNewTab =
    emptyState?.nightlyUpgradeEmptyLinkOpenInANewTab ?? nightlyUpgrade?.descriptionLinkOpenInANewTab;

  const sectionHowToEarnTracking = useMemo(
    () => resolveWalletEmbeddedTracking(nightlyUpgrade?.tracking),
    [nightlyUpgrade?.tracking]
  );
  const emptyHowToEarnTracking = useMemo(
    () =>
      resolveWalletEmptyHowToEarnTracking(
        emptyState?.nightlyUpgradeEmptyTracking,
        emptyState?.tracking,
        nightlyUpgrade?.tracking
      ),
    [emptyState?.nightlyUpgradeEmptyTracking, emptyState?.tracking, nightlyUpgrade?.tracking]
  );

  const cards = useMemo<WalletNuaCardViewModel[]>(() => {
    return items.map(item => {
      const isApplied = item.status !== WalletNightlyUpgradeAwardsSectionStatus.Available;
      const statusLabel = getStatusLabel(item.status, common);
      const statusVariant = getStatusVariant(item.status);
      const quantityLabel = buildSectionQuantityLabel(item.count, nightlyUpgrade?.sectionHeading);

      return {
        id: item.id,
        eyebrow: isApplied ? quantityLabel : undefined,
        title: isApplied ? item.propertyName || quantityLabel : quantityLabel,
        expirationText: isApplied
          ? undefined
          : buildWalletAwardExpirationText(common?.expiresLabel, item.expirationDate, currentLocale),
        expirationUrgent: !isApplied && isWalletAwardExpiringSoon(item.expirationDate),
        statusLabel,
        statusVariant,
        statusKind: item.status,
        mediaImageSrc: isApplied ? item.propertyImageSrc : undefined,
        mediaImageAltText: isApplied ? item.propertyImageAltText : undefined,
      };
    });
  }, [
    items,
    common?.availableLabel,
    common?.approvedLabel,
    common?.requestPendingLabel,
    common?.expiresLabel,
    nightlyUpgrade?.sectionHeading,
    currentLocale,
  ]);

  const totalCount = getNuaTotalCount(items);
  const emptyImage = emptyState?.nightlyUpgradeEmptyDynamicImage;
  const emptyImageUrl = emptyImage?.assetPath || emptyState?.nightlyUpgradeEmptyImage;
  const emptyImageAlt =
    emptyImage?.altText || emptyState?.nightlyUpgradeEmptyImageAltText || emptyState?.nightlyUpgradeEmptyAltText || '';
  return (
    <StyledWalletNightlyUpgradeAwardsSection
      data-testid="wallet-nightly-upgrade-awards-section"
      data-component-name="o-account-WalletNightlyUpgradeAwardsSection"
      className="pt-5"
    >
      {items.length > 0 ? (
        <div className="wallet-nightly-upgrade-awards-section__heading-row">
          <Heading
            variation={Types.headingType.subtitle}
            fontSize={Types.size.large}
            titleText={nightlyUpgrade?.sectionHeading || ''}
            customClass="wallet-nightly-upgrade-awards-section__heading m-0"
          />
          {totalCount > 0 ? <span className="wallet-nightly-upgrade-awards-section__count">{totalCount}</span> : null}
        </div>
      ) : null}

      {cards.length > 0 &&
      (nightlyUpgrade?.description ||
        (nightlyUpgrade?.descriptionLinkLabel && (nightlyUpgrade?.descriptionLink || onHowToEarnClick))) ? (
        <p className="wallet-nightly-upgrade-awards-section__description t-font-m m-0">
          {nightlyUpgrade?.description ? <span>{nightlyUpgrade.description} </span> : null}
          {nightlyUpgrade?.descriptionLinkLabel && (nightlyUpgrade?.descriptionLink || onHowToEarnClick) ? (
            onHowToEarnClick ? (
              <Link
                linkClassName="wallet-nightly-upgrade-awards-section__description-link ml-1"
                text={nightlyUpgrade.descriptionLinkLabel}
                linkHref="#"
                trackingProperties={sectionHowToEarnTracking.trackingProperties}
                custom_click_track_value={sectionHowToEarnTracking.customClickTrackValue}
                callback={(event?: React.MouseEvent) => {
                  event?.preventDefault();
                  onHowToEarnClick();
                }}
              />
            ) : (
              <Link
                linkClassName="wallet-nightly-upgrade-awards-section__description-link ml-1"
                target={nightlyUpgrade.descriptionLinkOpenInANewTab ? '_blank' : '_self'}
                rel={nightlyUpgrade.descriptionLinkOpenInANewTab ? 'noopener noreferrer' : ''}
                text={nightlyUpgrade.descriptionLinkLabel}
                linkHref={nightlyUpgrade.descriptionLink}
                trackingProperties={sectionHowToEarnTracking.trackingProperties}
                custom_click_track_value={sectionHowToEarnTracking.customClickTrackValue}
              />
            )
          ) : null}
        </p>
      ) : null}

      {cards.length > 0 ? (
        <div className="wallet-nightly-upgrade-awards-section__tiles">
          {cards.map((card, index) => {
            const statusIconClass = getStatusIconClass(card.statusKind, common);
            const { trackingProperties, customClickTrackValue } = getTrackingWithCustomClickValue(
              nightlyUpgrade?.tileTracking,
              items[index]?.status ?? ''
            );
            return (
              <WalletAwardTile
                key={card.id}
                eyebrow={card.eyebrow}
                title={card.title}
                expirationText={card.expirationText}
                expirationUrgent={card.expirationUrgent}
                statusLabel={card.statusLabel}
                statusVariant={card.statusVariant}
                statusIconClass={statusIconClass}
                mediaIconClass={card.mediaImageSrc ? undefined : nightlyUpgrade?.nightlyUpgradeAwardIcon}
                mediaIconColor={card.mediaImageSrc ? undefined : memberIconColor}
                mediaImageSrc={card.mediaImageSrc}
                mediaImageAltText={card.mediaImageAltText}
                ariaLabel={card.title}
                testId="wallet-nightly-upgrade-award-tile"
                onClick={onAwardTileClick ? () => onAwardTileClick(items[index]) : undefined}
                trackingProperties={trackingProperties}
                customClickTrackValue={customClickTrackValue}
              />
            );
          })}
        </div>
      ) : emptyState?.nightlyUpgradeEmptyMessage ? (
        <div className="wallet-nightly-upgrade-awards-section__empty-state">
          <div className="wallet-nightly-upgrade-awards-section__empty-media" aria-hidden="true">
            {emptyImageUrl ? (
              <Image
                customClass="wallet-nightly-upgrade-awards-section__empty-image"
                altText={emptyImageAlt}
                defaultImageURL={emptyImageUrl}
                renditions={emptyImage?.renditions as never}
                dynamic={emptyImage?.dynamic}
              />
            ) : nightlyUpgrade?.nightlyUpgradeAwardIcon ? (
              <Icon iconClass={nightlyUpgrade.nightlyUpgradeAwardIcon} isDecorative ariaLabel="" />
            ) : null}
          </div>
          <div className="wallet-nightly-upgrade-awards-section__empty-text">
            {emptyState?.nightlyUpgradeEmptyHeading ? (
              <Text
                element={Types.tags.div}
                fontSize={Types.size.small}
                copyText={emptyState.nightlyUpgradeEmptyHeading}
                customClass="wallet-nightly-upgrade-awards-section__empty-heading t-font-weight-m"
              />
            ) : null}
            <Text
              element={Types.tags.div}
              fontSize={Types.size.small}
              copyText={emptyState.nightlyUpgradeEmptyMessage}
              customClass="wallet-nightly-upgrade-awards-section__empty-message m-0"
            />
          </div>
          {emptyCtaLabel && (emptyCtaLink || onHowToEarnClick) ? (
            <div className="wallet-nightly-upgrade-awards-section__empty-cta ml-3">
              {onHowToEarnClick ? (
                <Link
                  linkClassName="wallet-nightly-upgrade-awards-section__empty-cta-link"
                  text={emptyCtaLabel}
                  ariaLabel={emptyCtaLabel}
                  linkHref="#"
                  trackingProperties={emptyHowToEarnTracking.trackingProperties}
                  custom_click_track_value={emptyHowToEarnTracking.customClickTrackValue}
                  callback={(event?: React.MouseEvent) => {
                    event?.preventDefault();
                    onHowToEarnClick();
                  }}
                />
              ) : (
                <Link
                  linkClassName="wallet-nightly-upgrade-awards-section__empty-cta-link"
                  text={emptyCtaLabel}
                  ariaLabel={emptyCtaLabel}
                  linkHref={emptyCtaLink}
                  target={emptyCtaOpenInNewTab ? '_blank' : '_self'}
                  trackingProperties={emptyHowToEarnTracking.trackingProperties}
                  custom_click_track_value={emptyHowToEarnTracking.customClickTrackValue}
                />
              )}
              <Icon iconClass="icon-forward-arrow" isDecorative ariaLabel="" />
            </div>
          ) : null}
        </div>
      ) : null}
    </StyledWalletNightlyUpgradeAwardsSection>
  );
};
